/**
 * Interpolacja pozycji zdalnych graczy / NPC (~100 ms delay, §6.2).
 * Czysta logika — bez Phaser.
 */

/** Opóźnienie renderu względem najnowszego snapshotu (ms). */
export const INTERP_DELAY_MS = 100;

export type PosSample = {
  t: number;
  x: number;
  y: number;
};

const DEFAULT_MAX_SAMPLES = 24;

/** Dołącz próbkę; utrzymuj ograniczone okno. */
export function pushSample(
  buf: readonly PosSample[],
  sample: PosSample,
  maxSamples: number = DEFAULT_MAX_SAMPLES,
): PosSample[] {
  const next = buf.length === 0 ? [sample] : [...buf, sample];
  // monotonia czasu — odrzuć cofnięte
  if (buf.length > 0 && sample.t < buf[buf.length - 1]!.t) {
    return [...buf];
  }
  if (next.length <= maxSamples) return next;
  return next.slice(next.length - maxSamples);
}

/**
 * Pozycja w czasie `renderTime` (zwykle now - INTERP_DELAY_MS).
 * Lerp między otaczającymi próbkami; clamp do krańców bufora.
 */
export function interpolateAt(
  buf: readonly PosSample[],
  renderTime: number,
): { x: number; y: number } | null {
  if (buf.length === 0) return null;
  if (buf.length === 1) {
    return { x: buf[0]!.x, y: buf[0]!.y };
  }

  // przed pierwszą próbką
  if (renderTime <= buf[0]!.t) {
    return { x: buf[0]!.x, y: buf[0]!.y };
  }
  // po ostatniej
  const last = buf[buf.length - 1]!;
  if (renderTime >= last.t) {
    return { x: last.x, y: last.y };
  }

  for (let i = 0; i < buf.length - 1; i++) {
    const a = buf[i]!;
    const b = buf[i + 1]!;
    if (renderTime >= a.t && renderTime <= b.t) {
      const span = b.t - a.t;
      const u = span <= 0 ? 1 : (renderTime - a.t) / span;
      return {
        x: a.x + (b.x - a.x) * u,
        y: a.y + (b.y - a.y) * u,
      };
    }
  }
  return { x: last.x, y: last.y };
}

/** Czas renderu z opóźnieniem interp. */
export function renderTimeNow(nowMs: number, delayMs: number = INTERP_DELAY_MS): number {
  return nowMs - delayMs;
}
